import React, { useRef, useState } from "react";
import { Container, Button, Card, CardBody, Spinner, Modal, ModalBody } from "reactstrap";
import { perPageData, showToastMessage } from "common/service/CommonService";
import CommonOffcanvasLayout from "Components/Common/CommonOffcanvasLayout";
import tableStyles from "../../../../../src/assets/scss/common/table.module.scss";
import { useRoleService } from "modules/Library/Role/api/useRoleQuery";
import Reason from "common/utilities/Reason/Reason";
import DeleteModal from "Components/Common/DeleteModal";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import CommonButton from "../../../../Components/Common/CommonButton";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "../../../../common/constants/PermissionConstants";
import { RoleRightsRef, StudyRole, StudyRoleFormValues, StudyRoleRecord } from "../helper/role.types";
import { roleTableColumns } from "../helper/roleconstant";
import { queryClient } from "api/queryClient";
import AddEditRole from "modules/Library/Role/components/AddEditRole";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import { useNavigate } from "react-router-dom";
import { LIBRARY_PATHS } from "Routes/paths";

const RoleList: React.FC = () => {
  document.title = "Role | ZENTIXS VISTA";
  const navigate = useNavigate();
  // Query client

  //-------------------------------------------------------------------------------------------
  //-->Use STATE
  //-------------------------------------------------------------------------------------------

  const [searchText, setSearchText] = useState<string | null>(null);
  const [updatedRecord, setUpdatedRecord] =
    useState<StudyRoleFormValues | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [editRecord, setEditRecord] = useState<StudyRoleFormValues | null>(
    null,
  );
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [rightsOpen, setRightsOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<StudyRole | null>(null);
  //Reason Modal
  const [isReasonModal, setIsReasonModal] = useState<boolean>(false);
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);

  //Audit
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();

  const ToggleDeleteReason = (): void => setShowDeleteReason((prev) => !prev);

  const [selectedRecord, setSelectedRecord] = useState<StudyRoleRecord | null>(
    null,
  );
  const [currentPage, setCurrentPage] = useState<number>(1);

  //-------------------------------------------------------------------------------------------
  //--> Toggles
  //-------------------------------------------------------------------------------------------

  const toggle = (): void => setIsOpen(!isOpen);
  const toggleDeleteModal = (): void => setDeleteModal(!deleteModal);
  const toggleReasonModal = (): void => {
    setIsReasonModal((prev) => !prev);
  };

  // Role Service(s)
  const { SelectAllQuery, InsertMutation, UpdateMutation, DeleteMutation } =
    useRoleService({searchText, id: undefined, getDropdown: false, pageNumber: currentPage, pageSize: perPageData});

  //-------------------------------------------------------------------------------------------
  //--> Page Start
  //-------------------------------------------------------------------------------------------

  const {
    data: queryResponse,
    isLoading,
    isFetching,
    isError,
  } = SelectAllQuery as {
    data?: { data?: StudyRoleRecord[]; totalcount?: number };
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
  };

  const data: StudyRoleRecord[] = queryResponse?.data ?? [];

  /* Ref */
  const roleRightsRef = useRef<RoleRightsRef>(null);
  //-------------------------------------------------------------------------------------------
  //--> ON SEARCH
  //-------------------------------------------------------------------------------------------

  // const onSearch = (text: string | null) => {
  //   setSearchText(text);
  //   setCurrentPage(1);
  //   }
  // };

   const onSearch = (text: string | null) => {
    setSearchText(text);
    setCurrentPage(1);
  };

  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  //-------------------------------------------------------------------------------------------
  //--> On Add
  //-------------------------------------------------------------------------------------------

  const onAdd = (): void => {
    setEditRecord(null);
    toggle();
  };

  const onRights = (role: StudyRole) => {
    navigate(LIBRARY_PATHS.ASSIGN_RIGHTS.replace(":id", String(role.roleid)), {
      state: { role }
    });
  };
  //-------------------------------------------------------------------------------------------
  //--> On Add New Record
  //-------------------------------------------------------------------------------------------

  const onEdit = (item: StudyRoleRecord): void => {
    const formValues: StudyRoleFormValues = {
      roleid: item.roleid,
      title: item.title,
      description: item.description ?? null,
    };
    setEditRecord(formValues);
    toggle();
  };

  //-------------------------------------------------------------------------------------------
  //--> Record Update
  //-------------------------------------------------------------------------------------------

  const btnReasonClick = (reasonPayload: {
    id: number | null;
    reason: string;
  }) => {
    if (!editRecord || editRecord.roleid == null || !updatedRecord) return;

    const payload: StudyRoleRecord & { updatereason: string } = {
      roleid: editRecord.roleid,
      title: updatedRecord.title ?? "",
      description:
        updatedRecord.description !== "" ? updatedRecord.description : null,
      updatereason: reasonPayload.reason,
    };

    UpdateMutation.mutate(payload, {
      onSuccess: (response) => {
        queryClient.invalidateQueries({ queryKey: ["role-selectAll"] });
        showToastMessage("success", response?.message);
        toggleReasonModal();
        toggle();
        setEditRecord(null);
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  //-------------------------------------------------------------------------------------------
  //--> Record Insert / Update
  //-------------------------------------------------------------------------------------------

  const onSave = (values: StudyRoleFormValues): void => {
    if (editRecord?.roleid) {
      setUpdatedRecord(values);
      toggleReasonModal();
    } else {
      const payload: Omit<StudyRoleRecord, "roleid"> = {
        title: values.title ?? "",
        description: values.description,
      };

      InsertMutation.mutate(payload as StudyRoleRecord, {
        onSuccess: (response) => {
          queryClient.invalidateQueries({ queryKey: ["role-selectAll"] });
          showToastMessage("success", response?.message);
          toggle();
        },
        onError: (error: any) => {
          showToastMessage("error", error?.message ?? error);
          setEditRecord(null);
        },
      });
    }
  };

  //-------------------------------------------------------------------------------------------
  //--> Record Delete
  //------------------------------------------------------------------------------------------

  const handleDeleteClick = (item: StudyRoleRecord): void => {
    setSelectedRecord(item);
    setDeleteModal(true);
  };

  const confirmDelete = (reasonPayload: {
    id: number | null;
    reason: string;
  }): void => {
    if (!selectedRecord) return;
    const payload = {
      id: reasonPayload.id,
      updatereason: reasonPayload.reason,
    };
    DeleteMutation.mutate(payload, {
      onSuccess: (response) => {
        setCurrentPage(1);
        queryClient.invalidateQueries({ queryKey: ["role-selectAll"] });
        showToastMessage("success", response?.message);
        toggleDeleteModal();
        setShowDeleteReason(false);
        setSelectedRecord(null);
      },
      onError: (error: Error) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  //-------------------------------------------------------------------------------------------
  //--> Reset Form
  //------------------------------------------------------------------------------------------

  const handleSaveRights = () => {
    roleRightsRef.current?.save();
  };
  const btnReset = () => {
    toggle();
    setEditRecord(null);
  };

  const onAuditClick = () => {
    const payload = {
      modulename: "Role",
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Role_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };



  return (
    <div className="page-content">
      <Container fluid className="px-0">
        <Card className={tableStyles.card}>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <CommonSearch onSearch={onSearch} searchText={searchText} />
              <div className="d-flex gap-2">
                <CommonButton
                  module={PERMISSION_MODULES.ROLE}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  size="sm"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
                <CommonButton
                  color="primary"
                  size="sm"
                  onClick={onAdd}
                  outline
                  module={PERMISSION_MODULES.ROLE}
                  action={PERMISSION_ACTIONS.ADD}
                >
                  <i className="ri-add-line me-1"></i> Add
                </CommonButton>
              </div>
            </div>
            <CommonTableIndexWise
              moduleName={PERMISSION_MODULES.ROLE}
              columns={roleTableColumns}
              data={data}
              showStatus={false}
              actions={{
                onEdit,
                onDelete: handleDeleteClick,
                onRights,
              }}
              totalRecords={data[0]?.totalcount || data[0]?.total_count || 0}
              pageSize={perPageData}
              currentPage={currentPage}
              onPageChange={onPageChange}
              loading={isLoading || isFetching}
            />
          </CardBody>
        </Card>

        <CommonOffcanvasLayout
          isOpen={isOpen}
          toggle={btnReset}
          title={
            editRecord
              ? `Edit Study Role ( ${editRecord.title} )`
              : "Add Study Role"
          }
        >
          <AddEditRole
            existingData={editRecord}
            onSave={onSave}
            onCancel={btnReset}
            loading={InsertMutation.isPending}
          />
        </CommonOffcanvasLayout>

        {isReasonModal && editRecord?.roleid != null && (
          <Reason
            isOpen={isReasonModal}
            toggle={toggleReasonModal}
            action="Update"
            id={editRecord.roleid}
            click={btnReasonClick}
            isLoading={UpdateMutation.isPending}
          />
        )}

        {/* Delete Modal */}
        {deleteModal && (
          <DeleteModal
            isopen={deleteModal}
            toggle={toggleDeleteModal}
            recordId={selectedRecord?.roleid ?? 0}
            recordName={selectedRecord?.title}
            onDeleteClick={confirmDelete}
            isLoading={DeleteMutation.isPending}
            showDeleteReason={showDeleteReason}
            toggleDeleteReason={ToggleDeleteReason}
          />
        )}
      </Container>

      {/* File Preview Modal */}
      {isPreviewOpen && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size={"xl"}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}
    </div>
  );
};

export default RoleList;
